import React, { useState } from 'react';
import { 
    Dialog, 
    DialogContent, 
    Typography, 
    Button, 
    Box, 
    LinearProgress, 
    CircularProgress, 
    IconButton, 
    Paper, 
    Chip 
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import CloseIcon from '@mui/icons-material/Close';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PrintIcon from '@mui/icons-material/Print';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useLanguage } from '../context/LanguageContext';

export default function PwaInstallModal({ open, deferredPrompt, onClose, updateAvailable, onUpdateApp }) {
    const { t } = useLanguage();

    // Installation states
    const [installing, setInstalling] = useState(false);
    const [installProgress, setInstallProgress] = useState(0);
    const [installCompleted, setInstallCompleted] = useState(false);

    // Update states
    const [updating, setUpdating] = useState(false);
    const [updateProgress, setUpdateProgress] = useState(0);
    const [updateCompleted, setUpdateCompleted] = useState(false);

    // Handle Start PWA Installation
    const handleStartInstall = async () => {
        setInstalling(true);
        setInstallProgress(5);

        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const choice = await deferredPrompt.userChoice;
                console.log('PWA Prompt choice:', choice);
            } catch (e) {
                console.error('PWA prompt error:', e);
            }
        }

        let current = 5;
        const interval = setInterval(() => {
            current += Math.floor(Math.random() * 15) + 10;
            if (current >= 100) {
                current = 100;
                setInstallProgress(100);
                setInstallCompleted(true);
                clearInterval(interval);
                setTimeout(() => {
                    setInstalling(false);
                    setInstallCompleted(false);
                    setInstallProgress(0);
                    onClose();
                }, 1800);
            } else {
                setInstallProgress(current);
            }
        }, 220);
    };

    // Handle Start Online App Update with Circular Percentage Progress Animation
    const handleStartUpdate = () => {
        setUpdating(true);
        setUpdateProgress(5);

        let current = 5;
        const interval = setInterval(() => {
            current += Math.floor(Math.random() * 18) + 12;
            if (current >= 100) {
                current = 100;
                setUpdateProgress(100);
                setUpdateCompleted(true);
                clearInterval(interval);
                setTimeout(() => {
                    if (onUpdateApp) {
                        onUpdateApp();
                    } else {
                        window.location.reload(true);
                    }
                }, 1500);
            } else {
                setUpdateProgress(current);
            }
        }, 200);
    };

    // Auto-Update View (New Code Deployment Detected)
    if (updateAvailable) {
        return (
            <Dialog 
                open={true} 
                maxWidth="xs" 
                fullWidth 
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px -10px rgba(234, 88, 12, 0.35)',
                        border: '3px solid #ea580c'
                    }
                }}
            >
                {/* Solid Saffron Kesari Header Banner */}
                <Box sx={{
                    bgcolor: '#ea580c', // Solid Kesari Saffron (No Gradient)
                    p: 3,
                    color: '#ffffff',
                    textAlign: 'center',
                    position: 'relative'
                }}>
                    <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: '#10b981', // Solid Emerald Green Badge
                        border: '3px solid #ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                    }}>
                        <SystemUpdateIcon sx={{ fontSize: 34, color: '#ffffff' }} />
                    </Box>
                    <Chip 
                        label={t('pwa-update-badge')} 
                        size="small" 
                        sx={{ bgcolor: '#ffffff', color: '#ea580c', fontWeight: 900, fontSize: '0.65rem', mb: 1 }} 
                    />
                    <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
                        {t('pwa-update-title')}
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 3, textAlign: 'center', bgcolor: '#ffffff' }}>
                    {updating ? (
                        <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            {updateCompleted ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {t('pwa-update-done')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#059669', fontWeight: 700 }}>
                                        {t('pwa-reloading')}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Circular Progress Bar with Live Percentage Counter */}
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress 
                                            variant="determinate" 
                                            value={updateProgress} 
                                            size={90}
                                            thickness={5}
                                            sx={{ color: '#ea580c' }}
                                        />
                                        <Box sx={{
                                            top: 0,
                                            left: 0,
                                            bottom: 0,
                                            right: 0,
                                            position: 'absolute',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <Typography variant="h6" component="div" sx={{ fontWeight: 900, color: '#10b981' }}>
                                                {`${Math.round(updateProgress)}%`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {t('pwa-updating')}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 3, lineHeight: 1.6 }}>
                                {t('pwa-update-desc')}
                            </Typography>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleStartUpdate}
                                sx={{
                                    bgcolor: '#ea580c', // Solid Kesari Button
                                    color: '#ffffff',
                                    fontWeight: 900,
                                    py: 1.5,
                                    borderRadius: 3,
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    boxShadow: '0 6px 16px rgba(234, 88, 12, 0.4)',
                                    '&:hover': {
                                        bgcolor: '#c2410c'
                                    }
                                }}
                            >
                                {t('pwa-update-btn')}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={installing ? undefined : onClose}
            maxWidth="xs" 
            fullWidth 
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px -10px rgba(16, 185, 129, 0.35)',
                    border: '3px solid #10b981'
                }
            }}
        >
            {/* Top Bar with Close Icon */}
            {!installing && (
                <IconButton 
                    onClick={onClose} 
                    size="small" 
                    sx={{ position: 'absolute', right: 12, top: 12, color: '#ffffff', zIndex: 10, bgcolor: 'rgba(0,0,0,0.3)' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}

            {/* Solid Saffron Kesari Header Banner with Emerald Green Badge */}
            <Box sx={{
                bgcolor: '#ea580c', // Solid Kesari Saffron (No Gradient)
                p: 3.5,
                color: '#ffffff',
                textAlign: 'center'
            }}>
                <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#10b981', // Solid Emerald Green Badge
                    border: '3px solid #ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1.5,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>
                    <GetAppIcon sx={{ fontSize: 38, color: '#ffffff' }} />
                </Box>
                <Chip 
                    label={t('pwa-badge')} 
                    size="small" 
                    sx={{ bgcolor: '#ffffff', color: '#ea580c', fontWeight: 900, fontSize: '0.65rem', mb: 1 }} 
                />
                <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
                    {t('pwa-title')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.95, fontWeight: 600, display: 'block', mt: 0.5 }}>
                    {t('pwa-subtitle')}
                </Typography>
            </Box>

            <DialogContent sx={{ p: 3, bgcolor: '#ffffff' }}>
                {installing ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                        {installCompleted ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981' }} />
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                    {t('pwa-complete-title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#059669', fontWeight: 700 }}>
                                    {t('pwa-complete-desc')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                {/* Circular Progress Bar with Percentage Count */}
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress 
                                        variant="determinate" 
                                        value={installProgress} 
                                        size={84}
                                        thickness={5}
                                        sx={{ color: '#10b981' }}
                                    />
                                    <Box sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Typography variant="h6" component="div" sx={{ fontWeight: 900, color: '#ea580c' }}>
                                            {`${Math.round(installProgress)}%`}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    {t('pwa-installing')}
                                </Typography>
                                
                                <Box sx={{ width: '100%', position: 'relative' }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={installProgress} 
                                        sx={{ 
                                            height: 12, 
                                            borderRadius: 6, 
                                            bgcolor: '#fed7aa',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 6,
                                                bgcolor: '#10b981' // Solid Emerald Green Progress Bar
                                            }
                                        }} 
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box>
                        {/* Features List with Solid Distinct Badges */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: '#fdba74', bgcolor: '#fff7ed' }}>
                                <FlashOnIcon sx={{ color: '#ea580c' }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                                        {t('pwa-feature1-title')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        {t('pwa-feature1-desc')}
                                    </Typography>
                                </Box>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: '#a7f3d0', bgcolor: '#ecfdf5' }}>
                                <PrintIcon sx={{ color: '#10b981' }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                                        {t('pwa-feature2-title')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        {t('pwa-feature2-desc')}
                                    </Typography>
                                </Box>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: '#cbd5e1', bgcolor: '#f8fafc' }}>
                                <WifiOffIcon sx={{ color: '#0f172a' }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                                        {t('pwa-feature3-title')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        {t('pwa-feature3-desc')}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={onClose}
                                sx={{
                                    borderColor: '#cbd5e1',
                                    color: '#64748b',
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    py: 1.2,
                                    textTransform: 'none'
                                }}
                            >
                                {t('pwa-later-btn')}
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleStartInstall}
                                sx={{
                                    bgcolor: '#ea580c', // Solid Kesari Saffron Button
                                    color: '#ffffff',
                                    fontWeight: 900,
                                    borderRadius: 3,
                                    py: 1.2,
                                    fontSize: '0.95rem',
                                    textTransform: 'none',
                                    boxShadow: '0 6px 16px rgba(234, 88, 12, 0.4)',
                                    '&:hover': {
                                        bgcolor: '#c2410c'
                                    }
                                }}
                            >
                                {t('pwa-install-btn')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
